<template>
    <div class="pb-2 pt-4">
        <hr class="mb-10" />
        <h4 class="mt-4 text-2xl">{{ $t('investment_process.vermanlg.vib_investor_from_germany') }}</h4>
        <p class="mb-4 text-gray-300">({{ $t('investment_process.vermanlg.investments_information_sheet') }})</p>
        <p class="mb-4 text-sm text-gray-500">
            {{ $t('investment_process.vermanlg.personal_data_verification_note') }}
        </p>
        <div class="grid grid-cols-1 gap-4 pb-2 md:grid-cols-2">
            <BaseInputText
                id="vib_current_location"
                v-model="vibCurrentLocation"
                name="vib_current_location"
                type="email"
                :label="$t('investment_process.vermanlg.vib_current_location')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="vib_date"
                v-model="vibDate"
                name="vib_date"
                :readonly="true"
                :label="$t('investment_process.vermanlg.vib_date')"
                :label-font-bold="true"
                :required="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <h4 class="my-4 text-2xl font-bold">{{ $t('investment_process.please_confirm_your_details') }}</h4>
        <div class="pb-2">
            <BaseInputText
                id="vib_email"
                v-model="vibEmail"
                name="vib_email"
                type="email"
                :class="vibError === 'vib_email' ? 'border-red-500 focus:border-red-500' : ''"
                :label="$t('investment_process.vermanlg.vib_email')"
                :label-font-bold="true"
                :required="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <div class="grid grid-cols-1 gap-4 pb-2 md:grid-cols-2">
            <BaseInputText
                id="vib_first_name"
                v-model="vibFirstName"
                name="vib_first_name"
                :class="vibError === 'vib_first_name' ? 'border-red-500 focus:border-red-500' : ''"
                :label="$t('investment_process.vermanlg.vib_first_name')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="vib_last_name"
                v-model="vibLastName"
                name="vib_last_name"
                :class="vibError === 'vib_last_name' ? 'border-red-500 focus:border-red-500' : ''"
                :label="$t('investment_process.vermanlg.vib_last_name')"
                :label-font-bold="true"
                :required="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <div class="grid grid-cols-1 gap-4 pb-2 md:grid-cols-2">
            <BaseInputText
                id="vib_place_of_birth"
                v-model="vibPlaceOfBirth"
                name="vib_place_of_birth"
                :label="$t('investment_process.vermanlg.vib_place_of_birth')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="vib_date_of_birth"
                v-model="vibDateOfBirth"
                name="vib_date_of_birth"
                type="date"
                :label="$t('investment_process.vermanlg.vib_date_of_birth')"
                :label-font-bold="true"
                :required="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <div class="grid grid-cols-1 gap-4 pb-2 md:grid-cols-2">
            <BaseInputText
                id="vib_id_card_passport_number"
                v-model="vibIdCardPassportNumber"
                name="vib_id_card_passport_number"
                :label="$t('investment_process.vermanlg.vib_id_card_passport_number')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="issuing_authority"
                v-model="issuingAuthority"
                name="issuing_authority"
                :label="$t('investment_process.vermanlg.issuing_authority')"
                :label-font-bold="true"
                :required="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <div class="grid grid-cols-1 gap-4 pb-2 md:grid-cols-2">
            <BaseInputText
                id="vib_address"
                v-model="vibAddress"
                name="vib_address"
                :label="$t('investment_process.vermanlg.vib_address')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="vib_postal_code"
                v-model="vibPostalCode"
                name="vib_postal_code"
                :label="$t('investment_process.vermanlg.vib_postal_code')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="vib_city"
                v-model="vibCity"
                name="vib_city"
                :label="$t('investment_process.vermanlg.vib_city')"
                :label-font-bold="true"
                :required="true" />
            <BaseInputText
                id="vib_state"
                v-model="vibState"
                name="vib_state"
                :label="$t('investment_process.vermanlg.vib_state')"
                :label-font-bold="true"
                :required="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <div class="pb-2">
            <BaseInputText
                id="vib_tax_number"
                v-model="vibTaxNumber"
                name="vib_tax_number"
                type="email"
                :label="$t('investment_process.vermanlg.vib_tax_number')"
                :label-font-bold="true" />
        </div>
        <hr class="mb-10 mt-10" />
        <div class="relative flex items-start pt-2">
            <div class="flex h-6 items-center">
                <input
                    id="vib_investment_information"
                    v-model="vibInvestmentInformation"
                    aria-describedby="vib_investment_information"
                    name="vib_investment_information"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
            </div>
            <div class="ml-3 text-sm leading-6">
                <label for="vib_investment_information" class="font-semibold text-gray-900">
                    {{ $t('investment_process.vermanlg.vib_investment_information') }}
                </label>
            </div>
        </div>
        <hr class="mb-10 mt-10" />
        <template v-if="isUnder1k">
            <div class="relative flex items-start pt-2">
                <div class="flex h-6 items-center">
                    <input
                        id="vib_investment_under_1000"
                        v-model="vibInvestmentUnder1000"
                        aria-describedby="vib_investment_under_1000"
                        name="vib_investment_under_1000"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        @change="resetRelatedCheckboxes('vibInvestmentUnder1000')" />
                </div>
                <div class="ml-3 text-sm leading-6">
                    <label for="vib_investment_under_1000" class="font-semibold text-gray-900">
                        {{
                            $t('investment_process.vermanlg.vib_investment_under_1000', {
                                asset_title: asset_title,
                            })
                        }}
                    </label>
                </div>
            </div>
            <div class="flex h-auto w-full items-center justify-center py-6">
                <p>{{ $t('investment_process.or') }}</p>
            </div>
        </template>
        <template v-if="isUnder1k || isBetween1kAnd10k">
            <div class="relative flex items-start pt-2">
                <div class="flex h-6 items-center">
                    <input
                        id="vib_investment_financial_assets_over_10000"
                        v-model="vibInvestmentFinancialAssetsOver10000"
                        aria-describedby="vib_investment_financial_assets_over_10000"
                        name="vib_investment_financial_assets_over_10000"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        @change="resetRelatedCheckboxes('vibInvestmentFinancialAssetsOver10000')" />
                </div>
                <div class="ml-3 text-sm leading-6">
                    <label for="vib_investment_financial_assets_over_10000" class="font-semibold text-gray-900">
                        {{
                            $t('investment_process.vermanlg.vib_investment_financial_assets_over_10000', {
                                asset_title: asset_title,
                            })
                        }}
                    </label>
                </div>
            </div>
            <div class="flex h-auto w-full items-center justify-center py-6">
                <p>{{ $t('investment_process.or') }}</p>
            </div>
        </template>
        <div class="relative flex items-start pt-2">
            <div class="flex h-6 items-center">
                <input
                    id="vib_investment_income"
                    v-model="vibInvestmentIncome"
                    aria-describedby="vib_investment_income"
                    name="vib_investment_income"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    @change="resetRelatedCheckboxes('vibInvestmentIncome')" />
            </div>
            <div class="ml-3 text-sm leading-6">
                <label for="vib_investment_income" class="font-semibold text-gray-900">
                    <template v-if="!isOver10k">
                        {{
                            $t('investment_process.vermanlg.vib_investment_income_over_1k', {
                                asset_title: asset_title,
                            })
                        }}
                    </template>
                    <template v-if="isOver10k">
                        {{
                            $t('investment_process.vermanlg.vib_investment_income_over_10k', {
                                asset_title: asset_title,
                            })
                        }}
                    </template>
                </label>
            </div>
        </div>
        <hr class="mb-10 mt-10" />
        <div class="relative flex items-start pt-2">
            <div class="flex h-6 items-center">
                <input
                    id="vib_investment_limit"
                    v-model="vibInvestmentLimit"
                    aria-describedby="vib_investment_limit"
                    name="vib_investment_limit"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
            </div>
            <div class="ml-3 text-sm leading-6">
                <label for="vib_investment_limit" class="font-semibold text-gray-900">
                    {{ $t('investment_process.vermanlg.vib_investment_limit_part_1') }}
                    {{ asset_title }}
                    {{ $t('investment_process.vermanlg.vib_investment_limit_part_2') }}
                </label>
            </div>
        </div>
        <hr class="mb-10 mt-10" />
        <p class="pb-4 text-gray-500">
            <strong class="font-bold text-red-500">{{ $t('investment_process.note') }}</strong>
            {{ $t('investment_process.vermanlg.vermanlg_confirm_section_note') }}
        </p>
        <RightOfWithdrawalWarning />
        <ConsequencesOfRevocationWarning />
    </div>
</template>

<script setup lang="ts">
import moment from 'moment'
import { storeToRefs } from 'pinia'
import BaseInputText from '../../base/BaseInputText.vue'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { onMounted, watch } from 'vue'
import { computed } from 'vue'
import RightOfWithdrawalWarning from '../warning/RightOfWithdrawalWarning.vue'
import ConsequencesOfRevocationWarning from '../warning/ConsequencesOfRevocationWarning.vue'

const {
    investmentAmount,
    vibCurrentLocation,
    vibDate,
    vibEmail,
    vibFirstName,
    vibLastName,
    vibPlaceOfBirth,
    vibDateOfBirth,
    vibIdCardPassportNumber,
    issuingAuthority,
    vibAddress,
    vibPostalCode,
    vibCity,
    vibState,
    vibTaxNumber,
    vibInvestmentInformation,
    vibInvestmentUnder1000,
    vibInvestmentFinancialAssetsOver10000,
    vibInvestmentIncome,
    vibInvestmentLimit,
    vibError,
} = storeToRefs(useInvestmentProcessDataState())

const { asset_title } = getCampaignData().asset_data

const isUnder1k = computed<boolean>(() => {
    if (!investmentAmount || !investmentAmount.value) {
        return false
    } else {
        return investmentAmount.value < 1000
    }
})

const isBetween1kAnd10k = computed<boolean>(() => {
    if (!investmentAmount || !investmentAmount.value) {
        return false
    } else {
        return investmentAmount.value >= 1000 && investmentAmount.value < 10000
    }
})

const isOver10k = computed<boolean>(() => {
    if (!investmentAmount || !investmentAmount.value) {
        return false
    } else {
        return investmentAmount.value >= 10000
    }
})

function resetRelatedCheckboxes(
    checkboxName: 'vibInvestmentUnder1000' | 'vibInvestmentFinancialAssetsOver10000' | 'vibInvestmentIncome'
) {
    if (checkboxName !== 'vibInvestmentUnder1000') {
        vibInvestmentUnder1000.value = false
    }
    if (checkboxName !== 'vibInvestmentFinancialAssetsOver10000') {
        vibInvestmentFinancialAssetsOver10000.value = false
    }
    if (checkboxName !== 'vibInvestmentIncome') {
        vibInvestmentIncome.value = false
    }
}

onMounted(() => {
    vibDate.value = moment(new Date()).format('DD.MM.YYYY')
})

watch(vibEmail, () => {
    vibError.value = null
})

watch(vibFirstName, () => {
    vibError.value = null
})

watch(vibLastName, () => {
    vibError.value = null
})
</script>

<style scoped></style>
